import type * as React from 'react';
import MapBoxMap, { Marker, NavigationControl } from 'react-map-gl';

import { env } from '@/constants/env';
import { cn } from '@/lib/utils';
import type { useModal } from './new/use-modal';
import { Modal } from './new/modal';

const MAP_BOX_TOKEN = env.MAP_BOX_API_TOKEN;

export interface MapFullscreenModalProps extends React.PropsWithChildren {
  latitude?: number | null;
  longitude?: number | null;
  zoom?: number;
  bearing?: number;
  pitch?: number;
  popupInfo?: React.ReactNode;
  modal: ReturnType<typeof useModal>;
}

const MarkerIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg
    width="29"
    height="47"
    viewBox="0 0 29 47"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path
      d="M14.5 0C6.48929 0 0 6.57203 0 14.6849C0 29.3698 14.5 47 14.5 47C14.5 47 29 29.3802 29 14.6849C29 6.57203 22.5107 0 14.5 0ZM14.5 23.6782C9.59422 23.6782 5.61991 19.6532 5.61991 14.6849C5.61991 9.71655 9.59422 5.69157 14.5 5.69157C19.4058 5.69157 23.3801 9.71655 23.3801 14.6849C23.3801 19.6532 19.4058 23.6782 14.5 23.6782ZM8.88009 14.6849C8.88009 17.8294 11.3951 20.3764 14.5 20.3764C17.6049 20.3764 20.1199 17.8294 20.1199 14.6849C20.1199 11.5404 17.6049 8.99331 14.5 8.99331C11.3951 8.99331 8.88009 11.5404 8.88009 14.6849Z"
      fill="#07B1BE"
    />
  </svg>
);

export const MapFullscreenModal: React.FC<MapFullscreenModalProps> = ({
  latitude,
  longitude,
  zoom = 14,
  bearing = 0,
  pitch = 0,
  children,
  modal,
}) => {
  const hasCoords = !Number.isNaN(Number(latitude)) && !Number.isNaN(Number(longitude));

  return (
    <Modal {...modal} title="" className="w-[98vw] max-w-7xl">
      <Modal.Body className="max-h-[80vh] overflow-hidden p-2 sm:p-4 md:p-6">
        <div
          className={cn(
            'h-[80vh] w-full rounded-lg md:h-[75vh]',
            '[&_.mapboxgl-ctrl-group]:top-[50%] [&_.mapboxgl-ctrl-group]:flex [&_.mapboxgl-ctrl-group]:flex-col [&_.mapboxgl-ctrl-group]:gap-2 [&_.mapboxgl-ctrl-group]:bg-transparent [&_.mapboxgl-ctrl-group]:shadow-none',
            '[&_.mapboxgl-ctrl-zoom-in]:size-8 [&_.mapboxgl-ctrl-zoom-in]:!rounded-sm [&_.mapboxgl-ctrl-zoom-in]:bg-primary-light',
            '[&_.mapboxgl-ctrl-zoom-out]:size-8 [&_.mapboxgl-ctrl-zoom-out]:!rounded-sm [&_.mapboxgl-ctrl-zoom-out]:bg-primary-light',
            '[&_.mapboxgl-ctrl-bottom-right]:hidden [&_.mapboxgl-ctrl-compass]:hidden [&_.mapboxgl-ctrl-scale]:hidden [&_a.mapboxgl-ctrl-logo]:hidden'
          )}
        >
          {hasCoords ? (
            <MapBoxMap
              initialViewState={{
                latitude: Number(latitude),
                longitude: Number(longitude),
                zoom,
                bearing,
                pitch,
              }}
              mapStyle="mapbox://styles/mapbox/light-v9"
              mapboxAccessToken={MAP_BOX_TOKEN}
            >
              <NavigationControl position="top-left" />
              <Marker longitude={Number(longitude)} latitude={Number(latitude)} anchor="bottom">
                <MarkerIcon />
              </Marker>
              {children}
            </MapBoxMap>
          ) : null}
        </div>
      </Modal.Body>
    </Modal>
  );
};
